import {neverland as $, html, useState} from 'neverland';
import { buildRegistrationEntry, sendApprovalNotification } from '../lib/approval.js';

function getContactEmail(userData) {
  if (!userData.contacts || !Array.isArray(userData.contacts)) return null;
  for (let c of userData.contacts) {
    if (c.email && c.email.trim()) return c.email.trim();
  }
  return null;
}

const ConfirmOrgAccessPage = $(function(auth, setAuth, theme) {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [noContactEmail, setNoContactEmail] = useState(false);
  const [isContactPerson, setIsContactPerson] = useState(false);

  function getLogo() {
    if (theme && theme.logo && theme.logo !== '') {
      return '/static/img/logos-large/' + theme.logo;
    }
    return '/static/img/transparent-placeholder.png';
  }

  async function handleConfirm() {
    setError('');
    setLoading(true);

    const userData = auth.userData;
    const users = userData.users || [];
    const email = auth.userEmail?.toLowerCase();

    // Check max users
    const maxUsers = userData.max_users_allowed || 0;
    const activeUsers = users.filter(u => !u.removed);
    if (maxUsers > 0 && activeUsers.length >= maxUsers) {
      const contactEmail = getContactEmail(userData);
      setError(`Maximum user limit (${maxUsers}) reached. Please contact ${contactEmail || 'your administrator'} to request access.`);
      setLoading(false);
      return;
    }

    // If no contact email is on file, the first registrant must self-identify
    // as the primary contact before proceeding.
    const contactEmail = getContactEmail(userData);
    if (!contactEmail && !isContactPerson) {
      setNoContactEmail(true);
      setError('No contact email is on file for this Organisation Username. If you are the primary contact, tick the checkbox below and try again. Otherwise, please ask your organisation\'s contact person to register first.');
      setLoading(false);
      return;
    }

    // Add to ClientGroup.users (and set contact email if they are the contact person).
    // ST4S phase 3 (design §6.2): the registration split — the first ACTIVE
    // registrant is auto-approved and logs in; any subsequent registrant is
    // written status:'pending' and routed to the pending-approval screen (no
    // data session). The entry shape + first/subsequent decision live in the
    // shared pure helper approval.js so BOTH registration paths stay in sync.
    const nowIso = new Date().toISOString();
    const { entry, isFirst } = buildRegistrationEntry({ users, email, nowIso });
    try {
      const updatedUsers = [...users, entry];
      const updateFields = { users: updatedUsers };
      if (isContactPerson && !getContactEmail(userData)) {
        const contacts = userData.contacts && Array.isArray(userData.contacts) ? [...userData.contacts] : [];
        contacts.push({ email, position: 'Primary Contact' });
        updateFields.contacts = contacts;
      }
      await auth.db.collection('ClientGroups').updateOne(
        { _id: userData._id },
        { $set: updateFields }
      );
      userData.users = updatedUsers;
      if (updateFields.contacts) userData.contacts = updateFields.contacts;
    } catch (dbErr) {
      console.error('Failed to update ClientGroup users:', dbErr);
      setError('Failed to register with this organisation. Please try again.');
      setLoading(false);
      return;
    }

    // Fire-and-forget server-side notification (helpdesk FYI for first contact,
    // contact-person notification for a pending request). Option A2 two-hop
    // send (notifications/send -> signed ticket -> notifier Function URL); the
    // helper swallows all errors so email failure can NOT block registration.
    // Not awaited.
    const clientGroupId = userData._id && userData._id['$oid'] ? userData._id['$oid'] : userData._id;
    sendApprovalNotification(auth, entry, clientGroupId);

    if (isFirst) {
      // Complete login as before.
      setAuth(prev => ({
        ...prev,
        mfaPage: null,
        mfaVerified: true,
        userPassword: null
      }));
    } else {
      // Pending: route to the pending-approval screen. Do NOT set mfaVerified
      // (App.js only promotes to isLoggedIn when mfaVerified) so no data
      // session loads.
      setAuth(prev => ({
        ...prev,
        mfaPage: 'PendingApproval',
        userPassword: null
      }));
    }

    setLoading(false);
  }

  function goBack() {
    setAuth(prev => ({ ...prev, mfaPage: null, cognitoUser: null, userData: null }));
  }

  return html`
    <div class="centre-box login">
      <img src="${getLogo()}" class="squiggle logo-large" />

      <div id="login">
        <h2 style="margin-bottom: 10px; font-size: 1.2em;">Request Access</h2>
        <p style="margin-bottom: 12px; font-size: 1em; opacity: 0.8;">
          You're not currently registered with <strong>${auth.userData?.name || 'this organisation'}</strong>.
        </p>
        <p style="margin-bottom: 15px; font-size: 1em; opacity: 0.8;">
          Would you like to register your account (<strong>${auth.userEmail}</strong>) with this organisation?
        </p>

        <button id="login-submit" onclick=${handleConfirm} disabled=${loading}>
          ${loading ? 'Registering...' : html`<i class="fal fa-check"></i>&ensp;YES, REGISTER ME`}
        </button>

        ${noContactEmail ? html`
          <label class="contact-checkbox">
            <input type="checkbox" onchange=${(e) => setIsContactPerson(e.target.checked)} />
            <span>I am the primary contact for this organisation</span>
          </label>
        ` : ''}

        <button class="back-link" onclick=${goBack}>
          &larr; No, go back
        </button>

        ${error ? html`<div class="error"><strong>ERROR</strong><p>${error}</p></div>` : ''}
      </div>
    </div>
  `;
});

export default ConfirmOrgAccessPage;
